// Helpers to download and read the CSV templates for importing your own test data.
// There are three files, so no cell needs a ';' list: spans (details), members (each row
// linked to its span by name) and beoordelaars. The files open directly in Excel.

import { genereerWagwoord } from '../services/wagwoord';

// Escape one CSV cell: wrap it in quotes when it contains a comma, quote or newline
const sel = (waarde) => {
  const s = String(waarde ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const ry = (velde) => velde.map(sel).join(',');

// The span template: one row per span (details only). Members go in the members file.
export const bouSpanneCsv = () => {
  const koppe = ['Span', 'Projek beskrywing', 'Span bio'];
  const voorbeeld = [
    ['Bakkers van die Berg', 'Tradisionele melktert met vye', 'Ouma se resep, verfris.'],
    ['Rooibos & Room', 'Melktert met rooibos', 'Experimentele bakkers.']
  ];
  return [ry(koppe), ...voorbeeld.map(ry)].join('\r\n');
};

// The member template: one row per member. The span name in the first column links the
// member to its span, so each member has its own row (full name and role).
export const bouLedeCsv = () => {
  const koppe = ['Span', 'Vollenaam', 'Rol', 'Foto URL (opsioneel)'];
  const voorbeeld = [
    ['Bakkers van die Berg', 'Lien van Wyk', 'Kaptein', ''],
    ['Bakkers van die Berg', 'Pieter Botha', 'Bakker', ''],
    ['Rooibos & Room', 'Mari du Toit', 'Kaptein', '']
  ];
  return [ry(koppe), ...voorbeeld.map(ry)].join('\r\n');
};

// The beoordelaar template. A blank password means the app generates one (shown after import).
export const bouBeoordelaarsCsv = () => {
  const koppe = ['Gebruikersnaam', 'Wagwoord', 'Volle naam', 'Organisasie', 'E-pos'];
  const voorbeeld = [
    ['annelie', '', 'Annelie van Wyk', 'Universiteit Stellenbosch', 'annelie@voorbeeld.co.za'],
    ['bennie', '', 'Bennie Pretorius', 'Karoo-Toerisme', 'bennie@voorbeeld.co.za']
  ];
  return [ry(koppe), ...voorbeeld.map(ry)].join('\r\n');
};

// Parse CSV text into an array of rows (each row an array of cells). Handles quoted cells with
// commas, quotes ("") and newlines. The UTF-8 BOM (Excel adds it) is ignored.
export const leesCsv = (teks) => {
  const skoon = teks.replace(/^\uFEFF/, '');
  const rye = [];
  let ry = [];
  let sel = '';
  let inAanhaling = false;

  for (let i = 0; i < skoon.length; i += 1) {
    const karakter = skoon[i];
    if (inAanhaling) {
      if (karakter === '"') {
        if (skoon[i + 1] === '"') {
          sel += '"';
          i += 1;
        } else {
          inAanhaling = false;
        }
      } else {
        sel += karakter;
      }
    } else if (karakter === '"') {
      inAanhaling = true;
    } else if (karakter === ',') {
      ry.push(sel);
      sel = '';
    } else if (karakter === '\n' || karakter === '\r') {
      if (karakter === '\r' && skoon[i + 1] === '\n') i += 1;
      ry.push(sel);
      rye.push(ry);
      ry = [];
      sel = '';
    } else {
      sel += karakter;
    }
  }
  if (sel !== '' || ry.length > 0) {
    ry.push(sel);
    rye.push(ry);
  }
  return rye;
};

// Parse the span template: [{ naam, projek, bio }], skipping the header and empty rows
export const leesSpanne = (teks) =>
  leesCsv(teks)
    .slice(1)
    .map(r => ({
      naam: (r[0] || '').trim(),
      projek: (r[1] || '').trim(),
      bio: (r[2] || '').trim()
    }))
    .filter(s => s.naam);

// Parse the member template: [{ span, naam, rol, foto }], skipping the header and empty rows.
// The span name links the member to the span it belongs to.
export const leesLede = (teks) =>
  leesCsv(teks)
    .slice(1)
    .map(r => ({
      span: (r[0] || '').trim(),
      naam: (r[1] || '').trim(),
      rol: (r[2] || '').trim(),
      foto: (r[3] || '').trim()
    }))
    .filter(l => l.span && l.naam);

// Parse the beoordelaar template: [{ gebruikersnaam, wagwoord, volle_naam, organisasie, epos }],
// skipping the header and empty rows. A blank password gets a generated one (gegenereer: true),
// the same way as creating a beoordelaar by hand.
export const leesBeoordelaars = (teks) =>
  leesCsv(teks)
    .slice(1)
    .map(r => {
      const gebruikersnaam = (r[0] || '').trim().toLowerCase();
      const ingevoer = (r[1] || '').trim();
      return {
        gebruikersnaam,
        wagwoord: ingevoer || genereerWagwoord(),
        gegenereer: !ingevoer,
        volle_naam: (r[2] || '').trim(),
        organisasie: (r[3] || '').trim(),
        epos: (r[4] || '').trim()
      };
    })
    .filter(b => b.gebruikersnaam);

// Trigger a browser download of a text file (with a UTF-8 BOM so Excel shows accents correctly)
export const laaiLêerAf = (naam, inhoud) => {
  const blob = new Blob(['\uFEFF' + inhoud], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const aflaai = document.createElement('a');
  aflaai.href = url;
  aflaai.download = naam;
  document.body.appendChild(aflaai);
  aflaai.click();
  document.body.removeChild(aflaai);
  URL.revokeObjectURL(url);
};
